// Modal with title, detail copy, and actions (up to 3, at least 1)
import React, { useContext, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  type RestModalForwards,
  type RestModalMultiCTATypeII,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import {
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '@bbkFunnelComponents/modals/sections';
import { MultiCTAOtherActions } from '@bbkFunnelComponents/modals/v2/multi-cta-actions';
import { RefsContext } from '@bbkFunnelUtils/refs.context';

import './modal.scss';

type Props = {
  modalInfo: RestModalMultiCTATypeII;
  close: () => void;
  handleAction: (action: RestModalForwards) => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-bottom: 10px;
`;

export const MultiCTATypeII: ReactFCC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      requires: {
        info: { title, details, image },
        generic_actions = [],
      },
    },
    close,
    handleAction,
  } = props;
  const { mobileView } = useMediaQueries();

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);

  const header = <TitleTheme title={title} />;
  const copy = <CustomerHtml value={details} />;

  const multiCTAOtherActions = (
    <div
      className="display-grid action-items"
      css={css`
        gap: 1rem;
      `}
    >
      <MultiCTAOtherActions
        id={id}
        actions={generic_actions}
        handleAction={handleAction}
      />
    </div>
  );
  if (mobileView) {
    return (
      <ModalMobileInnerSpacer
        className="display-grid multi-cta-type-II mobile inner-spacer"
        css={css`
          overflow-y: auto;
        `}
      >
        <OfferTitleMobile title={header} close={close} />
        <img
          alt=""
          src={image}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            border-radius: 4px;
          `}
        />
        <CopyContent
          css={css`
            padding-top: 10px;
          `}
        >
          {copy}
        </CopyContent>
        {multiCTAOtherActions}
      </ModalMobileInnerSpacer>
    );
  }

  return (
    <ModalDesktopInnerSpacer
      css={css`
        padding: 0;
        max-width: 900px;
      `}
      className="multi-cta-type-II display-grid desktop inner-spacer"
    >
      <img
        ref={imageRef}
        alt=""
        src={image}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        `}
      />
      <div
        css={css`
          padding: 1rem 1.5rem 2rem 2rem;
        `}
        className="display-grid content-grid"
      >
        <OfferTitleMobile
          title={<TitleTheme title={header} ref={titleRef} />}
          close={close}
        />
        <CopyContent ref={descRef}>{copy}</CopyContent>
        {multiCTAOtherActions}
      </div>
    </ModalDesktopInnerSpacer>
  );
};
